import React, { useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import { useFormik } from "formik";
import { Button } from "primereact/button";
import InputText from "../../../../components/form-control/input-text";
import { useNavigate, useParams } from "react-router-dom";
import { Card } from 'primereact/card';
import apiFunctions from "../../../../services/api-service";
import useToast from "../../../../components/common/useToast";
import { AxiosError } from "axios";
import { Select, Spin } from "antd";
import { errorHandler } from "../../../../utils/HelperFunctions";
import { persistor } from "../../../../redux/store";
import { logout } from "../../../../redux/features/authSlice";
import { useDispatch } from "react-redux";
import { ArrowLeftIcon, ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import { Editor } from 'primereact/editor';
import { Skeleton } from "primereact/skeleton";
const { Option } = Select;

/* ── DAG Visualization ── */
const BOX_W = 130, BOX_H = 68, H_GAP = 40, V_GAP = 64, PAD = 24;

const getBaseName = (name: string) => name.split(" (Percentage:")[0].trim();

const parseStats = (name: string): { pct: string; pctl: string } | null => {
    const m = name.match(/\(Percentage:\s*([^,]+),\s*Percentile:\s*([^)]+)\)/);
    return m ? { pct: m[1].trim(), pctl: m[2].trim() } : null;
};

const wrapText = (name: string, maxChars = 16): string[] => {
    const words = name.split(" ");
    const lines: string[] = [];
    let cur = "";
    for (const w of words) {
        if ((cur + " " + w).trim().length > maxChars && cur) { lines.push(cur.trim()); cur = w; }
        else cur = (cur + " " + w).trim();
    }
    if (cur) lines.push(cur.trim());
    return lines;
};

export const EducationDAG: React.FC<{ rules: any[]; currentId?: string }> = ({ rules, currentId }) => {
    const getId = (r: any) => String(r.id || r._id);
    // In this app, parentIds = "next steps after this rule" (children in visual tree)
    // So edges go: rule → its parentIds (downward), roots = not in anyone's parentIds
    const normNext = (r: any): string[] => (r.parentIds || []).map((p: any) => String(p));

    const fullMap = new Map(rules.map(r => [getId(r), r]));

    // Build set of all IDs that appear in someone's parentIds (= not roots)
    const referencedIds = new Set<string>();
    for (const r of rules)
        for (const nid of normNext(r))
            if (fullMap.has(nid)) referencedIds.add(nid);

    // Filter to connected subgraph containing currentId
    let visibleIds: Set<string>;
    if (currentId && fullMap.has(currentId)) {
        visibleIds = new Set<string>([currentId]);

        // walk DOWN via parentIds (= next steps)
        const downQ = [currentId];
        while (downQ.length) {
            const nid = downQ.shift()!;
            const rule = fullMap.get(nid);
            for (const pid of normNext(rule)) {
                if (fullMap.has(pid) && !visibleIds.has(pid)) { visibleIds.add(pid); downQ.push(pid); }
            }
        }
        // walk UP — find rules that list currentId in their parentIds (they are "above" current)
        const upQ = [currentId];
        while (upQ.length) {
            const nid = upQ.shift()!;
            for (const r of rules.filter(r => normNext(r).includes(nid))) {
                const rid = getId(r);
                if (!visibleIds.has(rid)) { visibleIds.add(rid); upQ.push(rid); }
            }
        }
    } else {
        visibleIds = new Set(rules.map(r => getId(r)));
    }

    const visibleRules = rules.filter(r => visibleIds.has(getId(r)));
    const nodeMap = new Map(visibleRules.map(r => [getId(r), r]));

    // Edges: FROM rule TO each of its parentIds (downward arrows)
    const edges: { from: string; to: string }[] = [];
    for (const r of visibleRules)
        for (const nid of normNext(r))
            if (nodeMap.has(nid)) edges.push({ from: getId(r), to: nid });

    // Roots = visible nodes NOT in anyone's parentIds in visible set
    const visibleReferenced = new Set<string>();
    for (const r of visibleRules)
        for (const nid of normNext(r))
            if (nodeMap.has(nid)) visibleReferenced.add(nid);

    // Longest-path from roots downward (following parentIds)
    const layerOf = new Map<string, number>();
    const assign = (nodeId: string, layer: number) => {
        if ((layerOf.get(nodeId) ?? -1) < layer) {
            layerOf.set(nodeId, layer);
            const rule = nodeMap.get(nodeId);
            for (const nid of normNext(rule))
                if (nodeMap.has(nid)) assign(nid, layer + 1);
        }
    };
    visibleRules.filter(r => !visibleReferenced.has(getId(r))).forEach(r => assign(getId(r), 0));
    visibleRules.forEach(r => { if (!layerOf.has(getId(r))) layerOf.set(getId(r), 0); });

    // group by layer
    const maxLayer = Math.max(...Array.from(layerOf.values()), 0);
    const groups: string[][] = Array.from({ length: maxLayer + 1 }, () => []);
    layerOf.forEach((l, id) => groups[l].push(id));

    // Pre-compute each node's content so the box height can grow to fit it fully
    const lineH = 14, statH = 12;
    const nodeContent = new Map<string, { lines: string[]; stats: { pct: string; pctl: string } | null }>();
    visibleRules.forEach(r => {
        const nid = getId(r);
        const isLastParent = normNext(r).filter(x => nodeMap.has(x)).length === 0;
        nodeContent.set(nid, {
            lines: wrapText(getBaseName(r.name)),
            stats: isLastParent ? null : parseStats(r.name || ""),
        });
    });
    const maxContentH = Math.max(
        ...Array.from(nodeContent.values()).map(c =>
            c.lines.length * lineH + (c.stats ? 2 * statH + 4 : 0)
        ), 0
    );
    const boxH = Math.max(BOX_H, maxContentH + 20);

    // positions — centre each layer (vertically reversed: roots at bottom, deepest at top)
    const positions = new Map<string, { x: number; y: number }>();
    const maxRowW = Math.max(...groups.map(g => g.length)) * (BOX_W + H_GAP) - H_GAP;
    groups.forEach((nodes, l) => {
        const rowW = nodes.length * (BOX_W + H_GAP) - H_GAP;
        const startX = PAD + (maxRowW - rowW) / 2;
        nodes.forEach((id, i) => positions.set(id, {
            x: startX + i * (BOX_W + H_GAP),
            y: PAD + (maxLayer - l) * (boxH + V_GAP),
        }));
    });

    const svgW = maxRowW + PAD * 2;
    const svgH = (maxLayer + 1) * (boxH + V_GAP) - V_GAP + PAD * 2;

    // A "skip" edge jumps over one or more intermediate layers (e.g. 9+ → 11+ Art
    // bypassing 10+). Its elbow would otherwise pass directly behind an
    // intermediate layer's boxes and look like it originates there instead of
    // from the real source, so these are drawn on top, in a distinct color.
    const isSkipEdge = (from: string, to: string) => {
        const lf = layerOf.get(from) ?? 0, lt = layerOf.get(to) ?? 0;
        return Math.abs(lf - lt) > 1;
    };

    const renderEdge = (from: string, to: string, i: number, skip: boolean) => {
        const f = positions.get(from), t = positions.get(to);
        if (!f || !t) return null;
        const goingDown = f.y < t.y;
        const x1 = f.x + BOX_W / 2, y1 = goingDown ? f.y + boxH : f.y;
        const x2 = t.x + BOX_W / 2, y2 = goingDown ? t.y : t.y + boxH;
        const my = skip
            ? (goingDown ? y1 + (V_GAP / 2) : y1 - (V_GAP / 2)) // jog right next to the source, in the empty gap
            : (y1 + y2) / 2;
        return (
            <path
                key={`${skip ? "skip" : "edge"}-${i}`}
                d={`M${x1} ${y1} L${x1} ${my} L${x2} ${my} L${x2} ${y2}`}
                stroke={skip ? "#4f46e5" : "#555"}
                strokeWidth={skip ? 1.5 : 1}
                strokeDasharray={skip ? "5 3" : undefined}
                fill="none"
                markerEnd={skip ? "url(#dag-arrow-skip)" : "url(#dag-arrow)"}
            />
        );
    };

    return (
        <svg width={svgW} height={svgH} style={{ display: "block", minWidth: svgW }}>
            <defs>
                <marker id="dag-arrow" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto" markerUnits="userSpaceOnUse">
                    <path d="M0,0 L8,4 L0,8 z" fill="#555" />
                </marker>
                <marker id="dag-arrow-skip" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto" markerUnits="userSpaceOnUse">
                    <path d="M0,0 L8,4 L0,8 z" fill="#4f46e5" />
                </marker>
            </defs>
            {edges.map(({ from, to }, i) => !isSkipEdge(from, to) ? renderEdge(from, to, i, false) : null)}
            {Array.from(positions.entries()).map(([nodeId, pos]) => {
                const rule = nodeMap.get(nodeId);
                if (!rule) return null;
                const isCurrent = nodeId === currentId;
                const content = nodeContent.get(nodeId);
                const lines = content?.lines || [];
                const stats = content?.stats || null;
                const totalH = lines.length * lineH + (stats ? 2 * statH + 4 : 0);
                const startY = pos.y + boxH / 2 - totalH / 2;
                return (
                    <g key={nodeId}>
                        <title>{getBaseName(rule.name)}</title>
                        <rect x={pos.x} y={pos.y} width={BOX_W} height={boxH}
                            fill={isCurrent ? "#eef2ff" : "#fff"}
                            stroke={isCurrent ? "#4f46e5" : "#000"}
                            strokeWidth={isCurrent ? 2 : 1} rx={2} />
                        {lines.map((line, li) => (
                            <text key={li}
                                x={pos.x + BOX_W / 2}
                                y={startY + li * lineH + lineH / 2}
                                textAnchor="middle" dominantBaseline="middle"
                                style={{ fontSize: "11px", fontWeight: isCurrent ? 700 : 400, fill: "#111" }}>
                                {line}
                            </text>
                        ))}
                        {stats && (
                            <>
                                <text
                                    x={pos.x + BOX_W / 2}
                                    y={startY + lines.length * lineH + 4 + statH / 2}
                                    textAnchor="middle" dominantBaseline="middle"
                                    style={{ fontSize: "9px", fill: "#6b7280" }}>
                                    {`Percentage: ${stats.pct}`}
                                </text>
                                <text
                                    x={pos.x + BOX_W / 2}
                                    y={startY + lines.length * lineH + 4 + statH + statH / 2}
                                    textAnchor="middle" dominantBaseline="middle"
                                    style={{ fontSize: "9px", fill: "#6b7280" }}>
                                    {`Percentile: ${stats.pctl}`}
                                </text>
                            </>
                        )}
                    </g>
                );
            })}
            {edges.map(({ from, to }, i) => isSkipEdge(from, to) ? renderEdge(from, to, i, true) : null)}
        </svg>
    );
};

const EducationRuleCreate: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { showToast }: any = useToast();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(!!id);

    const selectEducationRuleRef = useRef<any>(null);
    const [educationRuleOptions, setEducationRuleOptions] = useState<any[]>([]);
    const [allRulesForTree, setAllRulesForTree] = useState<any[]>([]);
    const [dagRules, setDagRules] = useState<any[]>([]);
    const buildTreeData = (rules: any[]) => setDagRules(rules);

    const [firstEducationRule, setFirstEducationRule] = useState(0);
    const [rows] = useState(10);
    const [searchEducationRuleValue, setSearchCountryValue] = useState('');
    const [fetchingEducationRule, setFetchingEducationRule] = useState(false);
    const [totalRecordsCountry, setTotalRecordsCountry] = useState(0);

    const validationSchema = Yup.object().shape({
        name: Yup.string().required("Name is required!"),
        minPercentage: Yup.number()
            .typeError("Must be a number")
            .required("Minimum Percentage is required"),
        maxPercentage: Yup.number()
            .typeError("Must be a number")
            .required("Minimum Percentage is required"),
        maxPercentile: Yup.number()
            .typeError("Must be a number")
            .required("Minimum Percentage is required"),
        minPercentile: Yup.number()
            .typeError("Must be a number")
            .required("Minimum Percentage is required"),
        content: Yup.string().required("Content is required!"),
    });

    const initialValue = {
        name: "",
        content: "",
        minPercentage: "",
        maxPercentage: "",
        minPercentile: "",
        maxPercentile: "",
        parentIds: []
    };

    const formik = useFormik({
        initialValues: initialValue,
        validationSchema: validationSchema,

        onSubmit: async (values: any) => {
            let payload = {
                name: values.name,
                content: values.content,
                minPercentage: Number(values.minPercentage),
                maxPercentage: Number(values.maxPercentage),
                minPercentile: Number(values.minPercentile),
                maxPercentile: Number(values.maxPercentile),
                parentIds: values.parentIds
            }
            if (id) {
                await apiFunctions.put(`/educationrule/${id}`, payload)
                    .then((response) => {
                        if (response && response.data && response.data.success) {
                            showToast({
                                severity: "success",
                                summary: "Success",
                                detail: response.data.message,
                                life: 3000,
                            });
                        } else {
                            showToast({
                                severity: "error",
                                summary: "Error",
                                detail: response.data.message,
                                life: 3000,
                            });
                        }
                    })
                    .catch((err: AxiosError) => {
                        if (err.status === 401) {
                            dispatch(logout());
                            persistor.purge();
                            navigate("/admin/login");
                        }
                        errorHandler(err);
                    });
            } else {
                await apiFunctions.post(`/educationrule`, payload)
                    .then((response) => {
                        if (response && response.data && response.data.success) {
                            showToast({
                                severity: "success",
                                summary: "Success",
                                detail: response.data.message,
                                life: 3000,
                            });
                        } else {
                            showToast({
                                severity: "error",
                                summary: "Error",
                                detail: response.data.message,
                                life: 3000,
                            });
                        }
                    })
                    .catch((err: AxiosError) => {
                        if (err.status === 401) {
                            dispatch(logout());
                            persistor.purge();
                            navigate("/admin/login");
                        }
                        errorHandler(err);
                    });
            }
        },
    });

    const fetchEducationRules = async (reset = false, selectIds?: string) => {
        if (fetchingEducationRule) return;
        setFetchingEducationRule(true);
        const start = reset ? 0 : firstEducationRule;
        try {
            const response = await apiFunctions.get(
                `/educationrule/dropdownlist?page=${start}&limit=${rows}&search=${searchEducationRuleValue || selectIds || ''}`
            );

            const newItems = response?.data?.data || [];
            const total = response?.data?.totalRecords || 0;

            if (reset) {
                setEducationRuleOptions(newItems);
                setFirstEducationRule(rows);
            } else {
                setEducationRuleOptions(prev => [...prev, ...newItems]);
                setFirstEducationRule(prev => prev + rows);
            }

            setTotalRecordsCountry(total);
        } finally {
            setFetchingEducationRule(false);
        }
    };

    useEffect(() => {
        setFirstEducationRule(0);
        fetchEducationRules(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchEducationRuleValue]);

    const handleCountryScroll = (e: any) => {
        const { scrollTop, offsetHeight, scrollHeight } = e.target;

        const nearBottom = scrollTop + offsetHeight >= scrollHeight - 10;
        const moreToLoad = educationRuleOptions.length < totalRecordsCountry;

        if (nearBottom && moreToLoad && !fetchingEducationRule) {
            fetchEducationRules();
        }
    };

    const handleCountrySearch = (value: any) => {
        setSearchCountryValue(value);
    };

    const handleCountryClear = () => {
        formik.setFieldValue("parentIds", []);

        setEducationRuleOptions([]);

        setSearchCountryValue("");

        setFirstEducationRule(0);

        // 🔥 Reload country list
        fetchEducationRules(true);
    };

    const getEducationRuleData = async (id: string) => {
        try {
            let url = `/educationrule/${id}`;
            await apiFunctions
                .get(url)
                .then((response) => {
                    if (response && response.data && response.data.success) {
                        formik.setFieldValue("name", response.data.data.name);
                        formik.setFieldValue("minPercentage", response.data.data.minPercentage);
                        formik.setFieldValue("maxPercentage", response.data.data.maxPercentage);
                        formik.setFieldValue("minPercentile", response.data.data.minPercentile);
                        formik.setFieldValue("maxPercentile", response.data.data.maxPercentile);
                        formik.setFieldValue("parentIds", response.data.data.parentIds);
                        formik.setFieldValue("content", response.data.data.content);
                    } else {
                        showToast({
                            severity: "error",
                            summary: "Error",
                            detail: response.data.message,
                            life: 3000,
                        });
                    }
                })
                .catch((err: AxiosError) => {
                    errorHandler(err);
                })
                .finally(() => {
                    setLoading(false);
                });
        } catch (error) {
            console.log(error);
        }
    }

    useEffect(() => {
        if (id) {
            getEducationRuleData(id);
        }
        // One-time fetch of ALL rules for the tree (not re-fetched on form edits)
        apiFunctions.get(`/educationrule/dropdownlist?page=1&limit=1000&search=`)
            .then(res => setAllRulesForTree(res?.data?.data || []))
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Append live percentage/percentile stats to the current rule's name so the
    // chart can display them (same format the dropdownlist API uses)
    const currentRuleName = () => {
        const base = formik.values.name || "Current Rule";
        const { minPercentage, maxPercentage, minPercentile, maxPercentile } = formik.values;
        if (minPercentage === "" && maxPercentage === "") return base;
        return `${base} (Percentage: ${minPercentage || 0}-${maxPercentage || 0}, Percentile: ${minPercentile || 0}-${maxPercentile || 0})`;
    };

    // Live tree preview — rebuilds on every parent selection so admin sees the
    // resulting tree (including transitive parents) before saving
    useEffect(() => {
        if (allRulesForTree.length === 0) {
            // fallback while full list is loading (create page only)
            if (id || (educationRuleOptions.length === 0 && !formik.values.name)) return;
            const currentRule = {
                id: "__current__",
                name: currentRuleName(),
                parentIds: formik.values.parentIds || [],
            };
            buildTreeData([...educationRuleOptions, currentRule]);
            return;
        }

        if (id) {
            // Edit page: override the saved rule with the in-progress form values
            let found = false;
            const merged = allRulesForTree.map(r => {
                if (String(r.id || r._id) === String(id)) {
                    found = true;
                    return { ...r, name: formik.values.name ? currentRuleName() : r.name, parentIds: formik.values.parentIds || [] };
                }
                return r;
            });
            if (!found) {
                merged.push({ id, name: currentRuleName(), parentIds: formik.values.parentIds || [] });
            }
            buildTreeData(merged);
        } else {
            // Create page: inject the unsaved rule into the full DB tree
            const currentRule = {
                id: "__current__",
                name: currentRuleName(),
                parentIds: formik.values.parentIds || [],
            };
            buildTreeData([...allRulesForTree, currentRule]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allRulesForTree, educationRuleOptions, formik.values.parentIds, formik.values.name,
        formik.values.minPercentage, formik.values.maxPercentage,
        formik.values.minPercentile, formik.values.maxPercentile]);

    const handleBack = () => {
        navigate('/admin/educationrule');
    }

    const dagContainerRef = useRef<HTMLDivElement>(null);

    const downloadTree = (format: "svg" | "png") => {
        const svg = dagContainerRef.current?.querySelector("svg");
        if (!svg) return;

        const clone = svg.cloneNode(true) as SVGSVGElement;
        clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
        // white background so the exported file is not transparent
        const bg = document.createElementNS("http://www.w3.org/2000/svg", "rect");
        bg.setAttribute("width", "100%");
        bg.setAttribute("height", "100%");
        bg.setAttribute("fill", "#ffffff");
        clone.insertBefore(bg, clone.firstChild);
        const svgStr = new XMLSerializer().serializeToString(clone);

        if (format === "svg") {
            const blob = new Blob([svgStr], { type: "image/svg+xml;charset=utf-8" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "education-rule-tree.svg";
            a.click();
            URL.revokeObjectURL(url);
            return;
        }

        const width = parseInt(svg.getAttribute("width") || "800");
        const height = parseInt(svg.getAttribute("height") || "600");
        const url = URL.createObjectURL(new Blob([svgStr], { type: "image/svg+xml;charset=utf-8" }));
        const img = new Image();
        img.onload = () => {
            const scale = 2; // 2x for sharp output
            const canvas = document.createElement("canvas");
            canvas.width = width * scale;
            canvas.height = height * scale;
            const ctx = canvas.getContext("2d");
            if (!ctx) return;
            ctx.scale(scale, scale);
            ctx.drawImage(img, 0, 0);
            URL.revokeObjectURL(url);
            canvas.toBlob((blob) => {
                if (!blob) return;
                const pngUrl = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = pngUrl;
                a.download = "education-rule-tree.png";
                a.click();
                URL.revokeObjectURL(pngUrl);
            }, "image/png");
        };
        img.src = url;
    };

    return (
        <>
            <div className="back-button" onClick={handleBack}>
                <ArrowLeftIcon className="w-6 h-6 text-black-700" /><h3 className="m-0">Back</h3>
            </div>
            <div className="page-title">
                {id ? (<h3>Edit Education Rule</h3>) : (<h3>Add Education Rule</h3>)}
            </div>
            <div className="pb-4">
                <form action="submit" onSubmit={formik.handleSubmit}>
                    <Card className="grid form-layout">
                    {loading ? (
                        <>
                            <div className="grid grid-cols-12 gap-4">
                                <div className="col-span-12 lg:col-span-6">
                                    <Skeleton width="60px" height="0.9rem" className="mb-2" />
                                    <Skeleton height="42px" />
                                </div>
                                <div className="col-span-12 lg:col-span-6">
                                    <Skeleton width="160px" height="0.9rem" className="mb-2" />
                                    <Skeleton height="42px" />
                                </div>
                            </div>
                            <div className="grid grid-cols-12 gap-4 mt-4">
                                <div className="col-span-12 lg:col-span-6">
                                    <Skeleton width="160px" height="0.9rem" className="mb-2" />
                                    <Skeleton height="42px" />
                                </div>
                                <div className="col-span-12 lg:col-span-6">
                                    <Skeleton width="170px" height="0.9rem" className="mb-2" />
                                    <Skeleton height="42px" />
                                </div>
                            </div>
                            <div className="grid grid-cols-12 gap-4 mt-4">
                                <div className="col-span-12 lg:col-span-6">
                                    <Skeleton width="150px" height="0.9rem" className="mb-2" />
                                    <Skeleton height="42px" />
                                </div>
                                <div className="col-span-12 lg:col-span-6">
                                    <Skeleton width="160px" height="0.9rem" className="mb-2" />
                                    <Skeleton height="42px" />
                                </div>
                            </div>
                            <div className="grid grid-cols-12 gap-4 mt-2">
                                <div className="col-span-12">
                                    <Skeleton width="70px" height="0.9rem" className="mb-2" />
                                    <Skeleton height="200px" borderRadius="8px" />
                                </div>
                            </div>
                            <div className="grid grid-cols-12 gap-4 mt-4">
                                <div className="field mb-4">
                                    <Skeleton width="90px" height="40px" />
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                        <div className="grid grid-cols-12 gap-4">
                            <div className="lg:col-span-6 col-span-12">
                                <InputText
                                    type="text"
                                    label="Name"
                                    placeholder=""
                                    modelValue={formik.values.name}
                                    disabled={false}
                                    autofocus={true}
                                    id="name"
                                    validation={
                                        formik.errors.name &&
                                        formik.touched.name && {
                                            $error: true,
                                            required: {
                                                $invalid: true,
                                                $message: formik.errors.name,
                                            },
                                        }
                                    }
                                    onUpdateModelValue={function (value: string): void {
                                        formik.setFieldValue("name", value);
                                    }}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-6">
                                <div className="field">
                                    <label htmlFor={`countryname`} className={`form-label`}>
                                        Parent Education Rules
                                    </label>
                                    <Select
                                        mode="multiple"
                                        ref={selectEducationRuleRef}
                                        allowClear
                                        onClear={() => handleCountryClear()}
                                        showSearch
                                        style={{ width: "100%", height: "42px" }}
                                        status={
                                            formik.touched.parentIds && formik.errors.parentIds
                                                ? "error"
                                                : ""
                                        }
                                        placeholder=""
                                        filterOption={false}
                                        onSearch={handleCountrySearch}
                                        onPopupScroll={handleCountryScroll}
                                        notFoundContent={fetchingEducationRule ? <Spin size="small" /> : 'No data'}
                                        value={formik.values.parentIds} // bind Formik value
                                        onChange={(value) => {
                                            formik.setFieldValue("parentIds", value);
                                        }}
                                        maxTagCount="responsive"
                                    >
                                        {educationRuleOptions.map(educationrule => (
                                            <Option key={educationrule.id} value={educationrule.id}>
                                                {educationrule.name}
                                            </Option>
                                        ))}
                                    </Select>
                                    {formik.touched.parentIds && typeof formik.errors.parentIds === "string" && (
                                        <span id="countryid-help" className="p-error text-left mt-2">
                                            {formik.errors.parentIds}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        {dagRules.length > 0 && (formik.values.parentIds?.length ?? 0) > 0 && (
                            <div className="col-span-12 mt-4">
                                <div className="flex items-center justify-between mb-3">
                                    <h4 className="form-label m-0">Education Rule Dependency Tree</h4>
                                    <div className="flex gap-2">
                                        <Button
                                            type="button"
                                            label="PNG"
                                            icon={<ArrowDownTrayIcon style={{ width: "16px", height: "16px", marginRight: "6px" }} />}
                                            outlined
                                            size="small"
                                            onClick={() => downloadTree("png")}
                                        />
                                        <Button
                                            type="button"
                                            label="SVG"
                                            icon={<ArrowDownTrayIcon style={{ width: "16px", height: "16px", marginRight: "6px" }} />}
                                            outlined
                                            size="small"
                                            onClick={() => downloadTree("svg")}
                                        />
                                    </div>
                                </div>
                                <div
                                    ref={dagContainerRef}
                                    style={{
                                        width: "100%",
                                        maxWidth: "100%",
                                        overflowX: "auto",
                                        WebkitOverflowScrolling: "touch",
                                        background: "#f9fafb",
                                        borderRadius: "8px",
                                        border: "1px solid #e5e7eb",
                                        padding: "12px",
                                    }}
                                >
                                    <EducationDAG rules={dagRules} currentId={id || "__current__"} />
                                </div>
                                <p className="text-xs text-gray-400 mt-2">
                                    <span style={{ display: "inline-block", width: 12, height: 12, background: "#eef2ff", border: "2px solid #4f46e5", borderRadius: 2, marginRight: 6, verticalAlign: "middle" }} />
                                    Current rule
                                </p>
                            </div>
                        )}
                        <div className="grid grid-cols-12 gap-4 mt-4">
                            <div className="lg:col-span-6 col-span-12">
                                <InputText
                                    type="text"
                                    label="Minimum Percentage"
                                    placeholder=""
                                    modelValue={formik.values.minPercentage}
                                    disabled={false}
                                    autofocus={true}
                                    id="minPercentage"
                                    validation={
                                        formik.errors.minPercentage &&
                                        formik.touched.minPercentage && {
                                            $error: true,
                                            required: {
                                                $invalid: true,
                                                $message: formik.errors.minPercentage,
                                            },
                                        }
                                    }
                                    onUpdateModelValue={function (value: string): void {
                                        formik.setFieldValue("minPercentage", value);
                                    }}
                                />
                            </div>
                            <div className="lg:col-span-6 col-span-12">
                                <InputText
                                    type="text"
                                    label="Maximum Percentage"
                                    placeholder=""
                                    modelValue={formik.values.maxPercentage}
                                    disabled={false}
                                    autofocus={false}
                                    id="maxPercentage"
                                    validation={
                                        formik.errors.maxPercentage &&
                                        formik.touched.maxPercentage && {
                                            $error: true,
                                            required: {
                                                $invalid: true,
                                                $message: formik.errors.maxPercentage,
                                            },
                                        }
                                    }
                                    onUpdateModelValue={function (value: string): void {
                                        formik.setFieldValue("maxPercentage", value);
                                    }}
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-12 gap-4 mt-4">
                            <div className="lg:col-span-6 col-span-12">
                                <InputText
                                    type="text"
                                    label="Minimum Percentile"
                                    placeholder=""
                                    modelValue={formik.values.minPercentile}
                                    disabled={false}
                                    autofocus={true}
                                    id="minPercentile"
                                    validation={
                                        formik.errors.minPercentile &&
                                        formik.touched.minPercentile && {
                                            $error: true,
                                            required: {
                                                $invalid: true,
                                                $message: formik.errors.minPercentile,
                                            },
                                        }
                                    }
                                    onUpdateModelValue={function (value: string): void {
                                        formik.setFieldValue("minPercentile", value);
                                    }}
                                />
                            </div>
                            <div className="lg:col-span-6 col-span-12">
                                <InputText
                                    type="text"
                                    label="Maximum Percentile"
                                    placeholder=""
                                    modelValue={formik.values.maxPercentile}
                                    disabled={false}
                                    autofocus={false}
                                    id="maxPercentile"
                                    validation={
                                        formik.errors.maxPercentile &&
                                        formik.touched.maxPercentile && {
                                            $error: true,
                                            required: {
                                                $invalid: true,
                                                $message: formik.errors.maxPercentile,
                                            },
                                        }
                                    }
                                    onUpdateModelValue={function (value: string): void {
                                        formik.setFieldValue("maxPercentile", value);
                                    }}
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-12 gap-4 flex items-center justify-between table-header mt-2">
                            <div className="col-span-12">
                                <h4 className="form-label m-0">Content</h4>
                                <Editor value={formik.values.content} onTextChange={(e) =>
                                    formik.setFieldValue("content", e.htmlValue ?? "")
                                } className="bg-white h-full lg:h-[calc(100vh-200px)] editor-custom" />
                            </div>
                        </div>
                        <div className="grid grid-cols-12 gap-4 mt-4">
                            <div className="field mb-4">
                                {id ? (
                                    <Button
                                        label="Edit"
                                        className="prime-button btn-login w-max"
                                        type="submit"
                                    ></Button>
                                ) : (
                                    <Button
                                        label="ADD"
                                        className="prime-button btn-login w-max"
                                        type="submit"
                                    ></Button>
                                )}
                            </div>
                        </div>
                        </>
                    )}
                    </Card>
                </form>
            </div>
        </>
    );
};

export default EducationRuleCreate;
